import { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Stack,
  Typography,
  IconButton,
  Alert,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import RefreshIcon from "@mui/icons-material/Refresh";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ReplayIcon from "@mui/icons-material/Replay";
import { useNavigate } from "react-router-dom";
import PrefixDownloadCard from "@/components/PrefixDownloadCard";
import { refreshDownloadedPrefixes } from "@/offline/offlineRefresh";
import { db } from "@/db";
import { useToastStore } from "@/ui/toastStore";
import {
  getDownloadedPrefixes,
  getStorageEstimate,
  refreshStorageEstimate,
  getGlobalSyncTimestamp,
  subscribeToStorageEstimate,
  type StorageEstimate,
} from "@/offline/offlineMeta";
import { usePrefixDownloader } from "@/offline/usePrefixDownloader";
import { useOfflineDownloadStore } from "@/offline/offlineDownloadStore";

const PREFIXES: Array<"HG" | "LS" | "WF" | "YO"> = ["HG", "LS", "WF", "YO"];

export default function OfflineManager() {
  const [syncMessage, setSyncMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [storageEstimate, setStorageEstimate] = useState<StorageEstimate | null>(null);
  const [downloaded, setDownloaded] = useState<string[]>([]);
  const [lastInboundSync, setLastInboundSync] = useState<string | null>(null);
  const pushToast = useToastStore((s) => s.push);
  const navigate = useNavigate();
  const prefixHooks = PREFIXES.map((p) => [p, usePrefixDownloader(p)] as const);
  const cancelAllDownloads = useOfflineDownloadStore((s) => s.cancelAllDownloads);
  const hasStartablePrefix = prefixHooks.some(
    ([, hook]) =>
      !hook.isDownloaded &&
      !["downloading", "queued", "cancelling", "deleting", "starting"].includes(hook.status)
  );

  const formatBytes = useCallback((bytes: number) => {
    if (!bytes) return "0 B";
    const units = ["B", "KB", "MB", "GB"];
    const idx = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
    const value = bytes / 1024 ** idx;
    return `${value.toFixed(value >= 10 ? 0 : 1)} ${units[idx]}`;
  }, []);

  const storageText = useCallback(() => {
    if (!storageEstimate) return "Calculating…";
    const used = formatBytes(storageEstimate.usageBytes);
    if (storageEstimate.quotaBytes) {
      const availableBytes = Math.max(storageEstimate.quotaBytes - storageEstimate.usageBytes, 0);
      const available = formatBytes(availableBytes);
      return `${used} used · ${available} available`;
    }
    return `${used} used`;
  }, [storageEstimate, formatBytes]);

  useEffect(() => {
    let mounted = true;
    Promise.all([getStorageEstimate(), getDownloadedPrefixes(), getGlobalSyncTimestamp()]).then(
      ([estimate, prefixes, lastSync]) => {
        if (!mounted) return;
        setStorageEstimate(estimate);
        setDownloaded(prefixes);
        setLastInboundSync(lastSync);
      }
    );
    const unsubscribe = subscribeToStorageEstimate((estimate) => setStorageEstimate(estimate));
    refreshStorageEstimate();
    return () => {
      mounted = false;
      unsubscribe?.();
    };
  }, []);

  // Reflect live download/delete state while on the page (including last delete)
  useEffect(() => {
    const downloadedNow = prefixHooks
      .filter(([, hook]) => hook.isDownloaded)
      .map(([p]) => p);
    const deletingNow = prefixHooks.find(
      ([, hook]) => hook.status === "deleting" && hook.isDownloaded
    );

    if (JSON.stringify(downloadedNow) !== JSON.stringify(downloaded)) {
      setDownloaded(downloadedNow);
    }

    // If the final prefix is being deleted (or none remain), reset last inbound sync display.
    if (downloadedNow.length === 0 || (downloadedNow.length === 1 && deletingNow)) {
      setLastInboundSync(null);
    }
  }, [prefixHooks, downloaded]);

  const handleRefresh = async () => {
    setLoading(true);
    try {
      await refreshDownloadedPrefixes();
      const latest = await getGlobalSyncTimestamp();
      setLastInboundSync(latest);
      const msg = "Notes synced";
      setSyncMessage(msg);
      pushToast("Notes synced", "success");
      localStorage.setItem("offlineLastDailyRefresh", new Date().toISOString());
    } catch (err: any) {
      const msg = err?.message ?? "Refresh failed";
      setSyncMessage(`Sync failed: ${msg}`);
      pushToast(`Sync failed: ${msg}`, "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const now = new Date();
    const todayNine = new Date();
    todayNine.setHours(9, 0, 0, 0);
    if (downloaded.length === 0) return;
    if (now < todayNine) return;
    const last = localStorage.getItem("offlineLastDailyRefresh");
    if (last && new Date(last) > todayNine) return;
    handleRefresh();
  }, [downloaded.length]);

  const downloadAll = async () => {
    for (const [, hook] of prefixHooks) {
      const { status, startDownload, isDownloaded } = hook;
      if (isDownloaded) continue;
      if (["downloading", "queued", "cancelling", "deleting"].includes(status)) continue;
      // Fire and forget so all prefixes enter the queue; the queue itself serializes work.
      void startDownload();
    }
  };

  const handleDeleteAll = async () => {
    cancelAllDownloads();
    setLoading(true);
    try {
      await db.delete();
      setStorageEstimate(null);
      setDownloaded([]);
      setLastInboundSync(null);
      pushToast("Offline data deleted. Reloading to recreate storage…", "success");
      setTimeout(() => window.location.reload(), 500);
    } catch (err: any) {
      pushToast(err?.message ?? "Failed to delete offline data", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 2, pb: 4 }}>
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
            Postcode prefixes
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Choose which areas to download for offline access.
          </Typography>
          {hasStartablePrefix && (
            <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1 }}>
              <Button variant="contained" size="small" onClick={downloadAll}>
                Download all
              </Button>
            </Stack>
          )}
          <Stack spacing={1}>
            {PREFIXES.map((p) => (
              <PrefixDownloadCard key={p} prefix={p} />
            ))}
          </Stack>
        </CardContent>
      </Card>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box sx={{ position: "relative" }}>
            <IconButton
              sx={{ position: "absolute", top: 0, right: 0 }}
              onClick={(e) => {
                e.stopPropagation();
                if (downloaded.length === 0) return;
                navigate("/sync");
              }}
              disabled={downloaded.length === 0}
              size="small"
            >
              <ChevronRightIcon />
            </IconButton>
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              justifyContent="space-between"
              sx={{ cursor: downloaded.length ? "pointer" : "default", gap: 2, flexWrap: "wrap" }}
              onClick={() => {
                if (downloaded.length === 0) return;
                navigate("/sync");
              }}
            >
              <Stack flex={1} spacing={0.5} minWidth={180}>
                <Typography variant="subtitle1" fontWeight={600}>
                  Sync notes
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {downloaded.length === 0
                    ? "Keep your offline data up to date."
                    : "Manage syncing new customer data to your device, and offline created notes to the server."}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Last inbound refresh: {lastInboundSync ? new Date(lastInboundSync).toLocaleString() : "Never"}
                </Typography>
              </Stack>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={(e) => {
                  e.stopPropagation();
                  handleRefresh();
                }}
                disabled={loading || downloaded.length === 0}
                size="small"
              >
                Refresh from server
              </Button>
            </Stack>
            {syncMessage && (
              <Alert
                severity="info"
                variant="outlined"
                sx={{ mt: 1, width: "100%" }}
                onClose={() => setSyncMessage(null)}
              >
                {syncMessage}
              </Alert>
            )}
          </Box>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Stack spacing={1.5}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography variant="subtitle1" fontWeight={600}>
                Storage (estimate, includes app)
              </Typography>
              <IconButton
                size="small"
                onClick={() => refreshStorageEstimate()}
                aria-label="Recalculate storage estimate"
              >
                <ReplayIcon fontSize="small" />
              </IconButton>
            </Stack>
            <Typography variant="body2" color="text.secondary">
              {storageText()}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={handleDeleteAll}
                disabled={loading}
              >
                Delete all
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}
